import { observer } from 'mobx-react-lite';
import * as React from 'react';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import CustomRecurrenceModal from './CustomRecurrence/CustomRecurrenceModal';
import getRecurrenceMenuItems from 'owa-calendar-quick-compose-view/lib/utils/recurrence/getRecurrenceMenuItems';
import getRecurrenceStrings from 'owa-recurrence-utils/lib/getRecurrenceStrings';
import getRecurrenceToRender from 'owa-calendar-meeting-forms-common-view/lib/utils/getRecurrenceToRender';
import getSelectedRecurrenceKey from 'owa-recurrence-utils/lib/getSelectedRecurrenceKey';
import getSelectedRecurrenceString from 'owa-recurrence-utils/lib/getSelectedRecurrenceString';
import isCustomRecurrenceType from 'owa-recurrence-utils/lib/isCustomRecurrenceType';
import RecurrenceMenu from 'owa-recurrence/lib/components/RecurrenceMenu';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import { initializeCustomRecurrenceForm } from 'owa-calendar-compose-form-actions';
import type { RecurrenceTypes } from 'owa-recurrence-utils/lib/RecurrenceTypes';
import generateBorderStyle from 'owa-calendar-forms-common/lib/utils/generateBorderStyle';
import {
    roomBookingNotSupportedDialogTitle,
    repeatNotSupportedDialogDescription,
} from './RoomBookingAssistant.locstring.json';
import confirmDisablingRoomBookingAssistant from '../utils/confirmDisablingRoomBookingAssistant';

export interface RecurrenceProps extends React.HTMLProps<HTMLDivElement> {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    onRecurrenceChangedCallback: (recurrence: RecurrenceType) => void;
    isNewEvent: boolean;
    disabled?: boolean;
}

let classNames = require('classnames');

export default observer(function RecurrenceComponent(props: RecurrenceProps) {
    const onRecurrenceMenuItemClick = (recurrenceSelection: RecurrenceTypes): void => {
        const { onRecurrenceChangedCallback, event } = props;
        const startDate = event.Start;
        const recurrenceToRender = getRecurrenceToRender(
            event.Recurrence,
            recurrenceSelection,
            startDate
        );
        // If user selected never on recurrence menu dropdown don't show recurrence modal and update store.event.Recurrence
        if (recurrenceToRender === null) {
            onRecurrenceChangedCallback(null);
        } else {
            confirmDisablingRoomBookingAssistant(
                event.IsRoomRequested,
                roomBookingNotSupportedDialogTitle,
                repeatNotSupportedDialogDescription,
                () => {
                    initializeCustomRecurrenceForm(recurrenceToRender, startDate);
                }
            );
        }
    };
    let { event, className, colorScheme, isNewEvent, disabled } = props;
    const recurrence = event.Recurrence;
    let currentRecurrenceString: string = getSelectedRecurrenceString(recurrence);
    let currentRecurrenceKey: string = getSelectedRecurrenceKey(
        recurrence,
        true /* includeCustom*/
    );
    let { recurrenceSummaryAriaLabel } = getRecurrenceStrings(event.Start.tz, event, recurrence);
    let isRecurrenceCustomType: boolean = isCustomRecurrenceType(recurrence);
    let placeHolderString: string = recurrence
        ? recurrenceSummaryAriaLabel
        : currentRecurrenceString;
    let recurrenceMenuItems = getRecurrenceMenuItems(
        isRecurrenceCustomType ? recurrenceSummaryAriaLabel : null,
        !isNewEvent /* isNeverDisabled */
    );
    const borderColorStyle = generateBorderStyle(colorScheme);
    return (
        <div className={classNames(className, borderColorStyle.bottomBorder)}>
            <RecurrenceMenu
                recurrenceMenuItems={recurrenceMenuItems}
                currentRecurrenceString={placeHolderString}
                currentRecurrenceKey={currentRecurrenceKey}
                onRecurrenceChangedCallback={onRecurrenceMenuItemClick}
                colorScheme={colorScheme}
                disabled={disabled}
            />
            <CustomRecurrenceModal event={event} colorScheme={props.colorScheme} />
        </div>
    );
});
